import { useCallback, useState, type ReactNode } from "react";

import type { BoxState, FaceAct, ListBox, Read, RowAct } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import {
  FOLLOW_UP_HOLDS, followUpHasBox,
  type FollowUp, type FollowUpLastLearning, type FollowUpLastTeaching, type FollowUpSession, type SessionWorkspace,
} from "@/lib/api/sessionPortal";
import { formatInCourseZone } from "@/lib/session-portal";

import { SESSION_COPY } from "./session-copy";
import { rowTaskOf, type SessionRowReport, type SessionTaskKind } from "./row-task";
import { rankOf } from "./session-ranking";
import { SessionRowTaskHost } from "./session-row-task";

const copy = SESSION_COPY.followUp;

export interface FollowUpValue {
  workspace: SessionWorkspace;
  followUp: FollowUp;
}

/** One row of what the last session left. A row whose session's Close opens in it names that session. */
interface FollowRow {
  key: string;
  title: string;
  facts: string;
  state: BoxState | null;
  act?: RowAct;
  /** The session whose Close this row opens in place (K3, CASE-019). */
  closes?: FollowUpSession;
  /** The server ranked this row's work: it starts open with its Task. */
  ranked?: boolean;
}

const NOT_READ: BoxState = { class: "failed", word: "Not read" };
const shortName = (session: Pick<FollowUpSession, "session_no" | "label">) =>
  session.session_no == null ? session.label?.trim() || "Session" : `Session ${session.session_no}`;
const fullName = (session: Pick<FollowUpSession, "session_no" | "label">) =>
  session.session_no != null && session.label?.trim() ? `Session ${session.session_no} · ${session.label.trim()}` : shortName(session);
const day = (session: FollowUpSession) => formatInCourseZone(session.starts_at, session.timezone, { weekday: "short", month: "short", day: "numeric" });
const recapState = (recap: { published: boolean; waiting: boolean }) => (recap.published ? (recap.waiting ? "edited, not republished" : "published") : "not published");

function lastOf(followUp: FollowUp, teaching: boolean) {
  const last = followUp.last_session.state === "ready" ? followUp.last_session.value : null;
  return {
    last,
    teachingLast: teaching ? (last as FollowUpLastTeaching | null) : null,
    learningLast: teaching ? null : (last as FollowUpLastLearning | null),
  };
}

function rowsOf({ workspace, followUp }: FollowUpValue, allSessionsPath: string): FollowRow[] {
  const teaching = workspace.viewer.capacities.includes("teaching");
  const { last, teachingLast, learningLast } = lastOf(followUp, teaching);
  const act = followUp.act;
  const name = last ? shortName(last) : "the last session";
  const outstanding = workspace.continuation?.outstanding;
  const rows: FollowRow[] = [];

  if (followUp.last_session.state === "unavailable") rows.push({ key: "last", title: copy.close, facts: copy.unread, state: NOT_READ });
  if (teachingLast) {
    const { roll, recap, review } = teachingLast;
    const unread = roll.state === "unavailable" || recap.state === "unavailable";
    const ranked = act.id === "close_last_session" || act.id === "publish_recap";
    rows.push({
      key: `close:${teachingLast.id}`,
      title: copy.close,
      facts: [
        roll.state === "ready" ? (roll.value.recorded ? copy.rollRecorded(roll.value.unrecorded_count) : copy.rollNotRecorded) : copy.rollUnread,
        recap.state === "ready" ? copy.recap(recapState(recap.value)) : copy.recapUnread,
      ].join(" · "),
      state: unread ? NOT_READ
        : act.id === "publish_recap" || (recap.state === "ready" && recap.value.waiting && teachingLast.status === "held") ? { class: "waiting", word: copy.recapWaits }
          : ranked || teachingLast.status === "ended" ? { class: "waiting", word: copy.notClosed(name) } : null,
      act: { verb: teachingLast.status === "held" ? copy.openHeld : copy.openClose, does: "open" },
      closes: teachingLast,
      ranked,
    });
    if (review.state === "ready") {
      rows.push({ key: "review", title: copy.review, facts: copy.awaiting(review.value.count), state: null, act: { verb: copy.reviewAct, does: "visit", visit: `session:${teachingLast.id}/review` } });
    } else if (review.state === "unavailable") {
      rows.push({ key: "review", title: copy.review, facts: copy.reviewUnread(name), state: NOT_READ, act: { verb: copy.reviewAct, does: "visit", visit: `session:${teachingLast.id}/review` } });
    }
  }
  if (learningLast) {
    const { attendance, recap, work } = learningLast;
    const attended = attendance.state === "ready" ? (attendance.value.status ? copy.youWere(attendance.value.status) : copy.attendanceNotRecorded) : copy.attendanceUnread;
    const recapWords = recap.state !== "ready" ? copy.learnerRecapUnread : recap.value.available ? copy.recapPublished : copy.noRecap;
    const readable = recap.state === "ready" && recap.value.available;
    rows.push({
      key: "after",
      title: copy.afterClass,
      facts: [learningLast.status === "held" ? attended : null, recapWords].filter(Boolean).join(" · "),
      state: attendance.state === "unavailable" || recap.state === "unavailable" ? NOT_READ : null,
      act: readable ? { verb: copy.readRecap, does: "exit", to: learningLast.destination.href } : undefined,
    });
    if (work.state === "ready") {
      const w = work.value;
      rows.push({
        key: "work",
        title: copy.yourWork,
        facts: copy.workWords(w.state, w.count),
        state: act.id === "revise" ? { class: "waiting", word: copy.returned } : null,
        act: w.state === "waiting_feedback" ? undefined : { verb: copy.openWork, does: "exit", to: w.destination.href },
      });
    } else if (work.state === "unavailable") {
      rows.push({ key: "work", title: copy.yourWork, facts: copy.workUnread(name), state: NOT_READ });
    }
  }
  if (outstanding?.state === "ready") {
    rows.push({
      key: "wide",
      title: copy.courseWide,
      facts: teaching ? copy.wideTeaching(outstanding.value.count) : copy.wideLearning(outstanding.value.count),
      state: null,
      act: { verb: copy.coursework, does: "exit", to: outstanding.value.destination.href },
    });
  } else if (outstanding?.state === "unavailable") {
    rows.push({ key: "wide", title: copy.courseWide, facts: copy.wideUnread, state: NOT_READ });
  }
  const earlier = followUp.earlier_sessions;
  if (earlier.state === "unavailable") rows.push({ key: "earlier", title: copy.earlier, facts: copy.earlierUnread, state: NOT_READ });
  if (earlier.state === "ready" && earlier.value.count > 3) {
    rows.push({ key: "earlier", title: copy.earlierMany(earlier.value.count), facts: copy.rollNotRecorded, state: null, act: { verb: copy.allSessions, does: "exit", to: allSessionsPath } });
  } else if (earlier.state === "ready") {
    for (const session of earlier.value.sessions) {
      const ranked = act.id === "close_older_session" && act.session_id === session.id;
      rows.push({
        key: `close:${session.id}`,
        title: fullName(session),
        facts: `${copy.endedOn(day(session))} · ${copy.rollNotRecorded}`,
        state: ranked ? { class: "waiting", word: copy.notClosed(shortName(session)) } : null,
        act: teaching ? { verb: copy.openClose, does: "open" } : undefined,
        closes: teaching ? session : undefined,
        ranked,
      });
    }
  }
  // A cancelled session is one row of text in time order, never the page.
  const cancelled = workspace.continuation?.foot?.cancelled;
  if (cancelled?.state === "ready") {
    for (const session of cancelled.value) {
      const when = formatInCourseZone(session.starts_at, session.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      rows.push({ key: `cancelled:${session.id}`, title: shortName(session), facts: copy.cancelledRow(when), state: null });
    }
  }
  return rows;
}

/** The box's own state: the server's ranked act names it; otherwise what the rows say. */
function stateOf({ workspace, followUp }: FollowUpValue, rows: FollowRow[]): BoxState {
  const teaching = workspace.viewer.capacities.includes("teaching");
  const { last, teachingLast, learningLast } = lastOf(followUp, teaching);
  const act = followUp.act;
  const name = last ? shortName(last) : "";
  const older = followUp.earlier_sessions.state === "ready" ? followUp.earlier_sessions.value.sessions.find((session) => session.id === act.session_id) : undefined;
  if (act.id === "close_last_session" && last) return { class: "waiting", word: copy.notClosed(name) };
  if (act.id === "publish_recap") return { class: "waiting", word: copy.recapWaits };
  if (act.id === "review_attempts" && teachingLast?.review.state === "ready") return { class: "waiting", word: copy.awaiting(teachingLast.review.value.count) };
  if (act.id === "close_older_session" && older) return { class: "waiting", word: copy.notClosed(shortName(older)) };
  if (act.id === "revise") return { class: "waiting", word: copy.returned };
  if (rows.some((row) => row.state?.class === "failed")) return { class: "failed", word: copy.notAllRead };
  // Nothing ranked, so the rows say what still waits, in the order the work happens.
  if (teachingLast && (teachingLast.status === "ended" || (teachingLast.roll.state === "ready" && !teachingLast.roll.value.recorded))) {
    return { class: "waiting", word: copy.notClosed(name) };
  }
  if (teachingLast?.recap.state === "ready" && teachingLast.recap.value.waiting) return { class: "waiting", word: copy.recapWaits };
  if (teachingLast?.review.state === "ready") return { class: "waiting", word: copy.awaiting(teachingLast.review.value.count) };
  const earlier = followUp.earlier_sessions.state === "ready" ? followUp.earlier_sessions.value.count : 0;
  if (earlier) return { class: "waiting", word: copy.earlierMany(earlier) };
  if (learningLast?.work.state === "ready") return { class: "quiet", word: copy.workWords(learningLast.work.value.state, learningLast.work.value.count) };
  const outstanding = workspace.continuation?.outstanding;
  if (outstanding?.state === "ready") {
    return { class: "quiet", word: teaching ? copy.wideTeaching(outstanding.value.count) : copy.wideLearning(outstanding.value.count) };
  }
  return { class: "done", word: copy.nothing };
}

/** The face's one line: the rows' own words, folded (S06). */
function foldedOf({ workspace, followUp }: FollowUpValue): string {
  const teaching = workspace.viewer.capacities.includes("teaching");
  const { last, teachingLast, learningLast } = lastOf(followUp, teaching);
  if (teachingLast) {
    return [
      teachingLast.roll.state === "ready" && teachingLast.roll.value.recorded ? copy.rollRecorded(null) : copy.rollNotRecorded,
      teachingLast.recap.state === "ready" ? copy.recap(recapState(teachingLast.recap.value)) : copy.recapUnread,
      teachingLast.review.state === "ready" ? copy.awaiting(teachingLast.review.value.count) : copy.noReview,
    ].join(" · ");
  }
  if (learningLast) {
    return [
      learningLast.status === "held" && learningLast.attendance.state === "ready"
        ? (learningLast.attendance.value.status ? copy.youWere(learningLast.attendance.value.status) : copy.attendanceNotRecorded)
        : null,
      learningLast.recap.state === "ready" && learningLast.recap.value.available ? copy.recapPublished : copy.noRecap,
    ].filter(Boolean).join(" · ");
  }
  return last ? fullName(last) : copy.earlier;
}

/** The server's held act as the face's one act (P2): Close opens its row, Review visits, Revise opens the work. */
function faceActOf({ workspace, followUp }: FollowUpValue): FaceAct | undefined {
  const teaching = workspace.viewer.capacities.includes("teaching");
  const { last, teachingLast, learningLast } = lastOf(followUp, teaching);
  const act = followUp.act;
  if (!FOLLOW_UP_HOLDS.has(act.id)) return undefined;
  if (act.id === "close_last_session" && last) return { verb: copy.closeSession(shortName(last)), does: "open" };
  if (act.id === "publish_recap" && last) return { verb: copy.publishRecap, does: "open" };
  if (act.id === "review_attempts" && teachingLast?.review.state === "ready") {
    return { verb: copy.reviewAttempts(teachingLast.review.value.count), does: "visit", visit: `session:${teachingLast.id}/review` };
  }
  if (act.id === "revise" && learningLast?.work.state === "ready") return { verb: copy.revise, does: "exit", to: learningLast.work.value.destination.href };
  const older = followUp.earlier_sessions.state === "ready" ? followUp.earlier_sessions.value.sessions.find((session) => session.id === act.session_id) : undefined;
  if (act.id === "close_older_session" && older) return { verb: copy.closeSession(shortName(older)), does: "open" };
  return undefined;
}

/**
 * S06 Follow up: a List of what the last session left, first on the page
 * when it left anything. Each row says its own words, and only a row that
 * differs is marked (K3). The teaching team's last session's Close opens in
 * its row, as an earlier unclosed session's does (CASE-019); its Review
 * visits `session:<last>/review` (O10). Your work and course-wide work open
 * their own page until A3's Hand in opens in the row. The face holds its
 * act at the rank the server's ranking read gives Follow up.
 */
export function useFollowUpBox({ read, quiet, allSessionsPath, courseworkPath }: {
  read: Read<SessionWorkspace>;
  /** Another box holds the act the person opened by hand, so Follow up offers none. */
  quiet: boolean;
  allSessionsPath: string;
  courseworkPath: string;
}): { box: ListBox<FollowUpValue, FollowRow> | null; hosts: ReactNode; onRowOpenChange: (box: string, key: string, open: boolean) => void } {
  const [reports, setReports] = useState<ReadonlyMap<string, SessionRowReport>>(() => new Map());
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  // The row whose Task the person committed. Its answer stays open in it, whatever
  // the re-read after the commit ranks next, until they open another row or close it.
  const [kept, setKept] = useState<string | null>(null);
  const onReport = useCallback((sessionId: string, _kind: SessionTaskKind, report: SessionRowReport) => {
    setReports((map) => new Map(map).set(sessionId, report));
  }, []);
  const onRowOpenChange = useCallback((box: string, key: string, open: boolean) => {
    if (box !== "follow-up") return;
    if (open) setOpened((keys) => (keys.has(key) ? keys : new Set(keys).add(key)));
    setKept((current) => (current !== null && (open ? key !== current : key === current) ? null : current));
  }, []);

  const workspace = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  const followUp = workspace?.continuation?.follow_up;
  if (!workspace || !followUp || !followUpHasBox(followUp)) return { box: null, hosts: null, onRowOpenChange };

  const value: FollowUpValue = { workspace, followUp };
  const rows = rowsOf(value, allSessionsPath);
  const keptRow = kept !== null && rows.some((row) => row.key === kept) ? kept : null;
  // The last session's Close is read with the page; an earlier one once its row is opened.
  const hosted = rows.filter((row) => row.closes && (row.ranked || row.key === keptRow || row.key === rows.find((r) => r.closes)?.key || opened.has(row.key)));
  const hosts = hosted.map((row) => (
    <SessionRowTaskHost key={row.closes!.id} sessionId={row.closes!.id} kind="close" courseworkPath={courseworkPath} onReport={onReport} />
  ));
  const reportOf = (row: FollowRow) => (row.closes ? reports.get(row.closes.id) : undefined);
  const listRead: Read<FollowUpValue> = read.status === "stale"
    ? { ...read, last: value }
    : { status: "ready", value, readAt: read.status === "ready" ? read.readAt : new Date().toISOString() };
  const act = quiet ? undefined : faceActOf(value);
  const { last } = lastOf(followUp, workspace.viewer.capacities.includes("teaching"));
  const when = last ? (last.status === "held" ? copy.held(day(last)) : copy.ended(day(last))) : null;

  const box: ListBox<FollowUpValue, FollowRow> = {
    kind: "list",
    id: "follow-up",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read: listRead,
    state: (current) => stateOf(current, rowsOf(current, allSessionsPath)),
    rows: (current) => rowsOf(current, allSessionsPath),
    row: {
      key: (row) => row.key,
      title: (row) => row.title,
      facts: (row) => row.facts,
      state: (row) => row.state,
      act: (row) => row.act,
      // A kept answer holds its row open over the server's next ranked row (F3, P5).
      startsOpen: (row) => (keptRow !== null ? row.key === keptRow : Boolean(row.ranked)),
      task: (row) => {
        const report = reportOf(row);
        if (report?.status !== "ready" || !report.box) return undefined;
        const task = rowTaskOf(report.box, row.key);
        return { ...task, commit: { ...task.commit, run: (requestId) => { setKept(row.key); return task.commit.run(requestId); } } };
      },
      detail: (row) => {
        if (!row.closes) return undefined;
        const report = reportOf(row);
        const name = shortName(row.closes);
        if (!report || report.status === "loading") return <p role="status" className="text-ui text-muted-foreground">{copy.reading(name)}</p>;
        if (report.status === "failed") return (
          <p className="flex flex-wrap items-center gap-x-3 text-ui text-foreground">
            {copy.readFailed(name)}
            <Button type="button" variant="subtle" size="sm" onClick={report.retry}>{copy.tryAgain}</Button>
          </p>
        );
        if (report.status === "refused") return <p className="text-ui text-foreground">{copy.readRefused(name)}</p>;
        if (report.stale) return (
          <p className="flex flex-wrap items-center gap-x-3 text-ui text-foreground" data-stale="">
            {copy.readStale(name, formatInCourseZone(report.stale.readAt, row.closes.timezone, { hour: "numeric", minute: "2-digit" }))}
            <Button type="button" variant="subtle" size="sm" onClick={report.stale.retry}>{copy.tryAgain}</Button>
          </p>
        );
        return undefined;
      },
    },
    detail: (current) => foldedOf(current),
    sub: last && when ? () => `${fullName(last)} · ${when}` : undefined,
    act,
    candidate: act ? { eligible: true, blocked: null, rank: rankOf(read, "follow-up") } : undefined,
    exits: [{ label: copy.show, reopens: true }],
    fold: "settled",
  };
  return { box, hosts, onRowOpenChange };
}
